import { SOURCE_KEY } from '@layouts/pages/CRM/kanban-board/formatters/lead-source-label';
import { ELeadSource } from '@layouts/pages/CRM/kanban-board/services/responses/lead.response';
import {
    AutomationSettings, EStageAction, EStageTrigger, StageAutomationInput,
} from './services/automation.service';

/// Cada automação lida como frase ("Quando o lead ficar 3 dias parado em Novo, ..."), escolhas em <b>.

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type RuleDraft = Pick<
    StageAutomationInput,
    | 'boardColumnId' | 'trigger' | 'idleDays' | 'hoursBefore' | 'action'
    | 'messageText' | 'taskDescription' | 'taskDueInDays' | 'targetColumnId'
    | 'assignUserId' | 'targetStatusId' | 'delayHours' | 'conditionMinValue'
    | 'conditionSource'
>;

interface Named {
    id: string;
    name: string;
}

export interface SentenceNames {
    columns: Named[];
    users: Named[];
    statuses: Named[];
}

export interface SentencePart {
    text: string;
    strong: boolean;
}

const TRIGGER_KEY: Record<EStageTrigger, string> = {
    [EStageTrigger.OnEnter]: 'automacoes.fraseEntrar',
    [EStageTrigger.AfterDaysIdle]: 'automacoes.fraseParado',
    [EStageTrigger.AfterDaysNoReply]: 'automacoes.fraseSemResposta',
    [EStageTrigger.OnWon]: 'automacoes.fraseGanho',
    [EStageTrigger.OnLost]: 'automacoes.frasePerdido',
    [EStageTrigger.BeforeSchedule]: 'automacoes.fraseAntesAgendamento',
    [EStageTrigger.AfterScheduleDone]: 'automacoes.fraseAgendamentoConcluido',
};

const SNIPPET_LENGTH = 40;

/// O começo do texto, numa linha só: a mensagem inteira não cabe numa frase.
export const snippet = (text?: string | null) => {
    const flat = (text ?? '').replace(/\s+/g, ' ').trim();

    if (!flat) return null;

    if (flat.length <= SNIPPET_LENGTH) return `"${flat}"`;

    // Corta no último espaço: "não cabe i…" lê como erro de digitação.
    const cut = flat.slice(0, SNIPPET_LENGTH + 1);
    const space = cut.lastIndexOf(' ');
    const head = space > SNIPPET_LENGTH / 2 ? cut.slice(0, space) : cut.slice(0, SNIPPET_LENGTH);

    return `"${head.trimEnd()}…"`;
};

const nameOf = (list: Named[], id?: string | null) => list.find((item) => item.id === id)?.name;

const currency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });

export const ruleSentence = (t: Translate, rule: RuleDraft, names: SentenceNames) => {
    const unknown = t('automacoes.fraseIndefinido');
    const column = nameOf(names.columns, rule.boardColumnId) ?? unknown;

    const trigger = t(TRIGGER_KEY[rule.trigger], {
        coluna: column,
        dias: rule.idleDays,
        horas: rule.hoursBefore,
    });

    const action = {
        [EStageAction.SendMessage]: () => t('automacoes.fraseEnviar', {
            mensagem: snippet(rule.messageText) ?? t('automacoes.fraseUmaMensagem'),
        }),
        [EStageAction.CreateTask]: () => {
            const task = snippet(rule.taskDescription);
            const due = rule.taskDueInDays > 0
                ? t('automacoes.fraseDaquiDias', { dias: rule.taskDueInDays })
                : t('automacoes.fraseHoje');

            return task
                ? t('automacoes.fraseTarefa', { tarefa: task, prazo: due })
                : t('automacoes.fraseTarefaSemTexto', { prazo: due });
        },
        [EStageAction.Assign]: () => t('automacoes.fraseAtribuir', {
            pessoa: nameOf(names.users, rule.assignUserId) ?? t('automacoes.fraseQuemTemMenos'),
        }),
        [EStageAction.MoveToColumn]: () => t('automacoes.fraseMover', {
            destino: nameOf(names.columns, rule.targetColumnId) ?? unknown,
        }),
        [EStageAction.SetStatus]: () => t('automacoes.fraseStatus', {
            status: nameOf(names.statuses, rule.targetStatusId) ?? unknown,
        }),
    }[rule.action]();

    const extras = [
        rule.delayHours > 0 ? t('automacoes.fraseAtraso', { horas: rule.delayHours }) : null,
        rule.conditionMinValue
            ? t('automacoes.fraseValorMinimo', { valor: currency(rule.conditionMinValue) })
            : null,
        rule.conditionSource !== null && rule.conditionSource !== undefined
            ? t('automacoes.fraseOrigem', {
                origem: t(SOURCE_KEY[rule.conditionSource as ELeadSource]),
            })
            : null,
    ].filter(Boolean);

    return [t('automacoes.fraseRegra', { gatilho: trigger, acao: action }), ...extras].join(', ');
};

export const assignSentence = (t: Translate) => t('automacoes.fraseDistribuir');

export const replySentence = (t: Translate, config: AutomationSettings) => {
    const sentence = t('automacoes.fraseResponder', {
        mensagem: snippet(config.autoReplyMessage) ?? t('automacoes.fraseUmaMensagem'),
    });

    const own = (config.autoReplyChannels ?? [])
        .map((channel) => t(SOURCE_KEY[channel.source as ELeadSource]));

    if (own.length === 0) return sentence;

    return `${sentence}, ${t('automacoes.fraseExcetoCanais', { canais: own.join(', ') })}`;
};

export const sentenceParts = (sentence: string): SentencePart[] =>
    sentence
        .split(/(<b>.*?<\/b>)/)
        .filter((piece) => piece !== '')
        .map((piece) => {
            const strong = piece.startsWith('<b>') && piece.endsWith('</b>');

            return { text: strong ? piece.slice(3, -4) : piece, strong };
        });

export const plainSentence = (sentence: string) => sentence.replace(/<\/?b>/g, '');
